import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';

// The deliverables list's target collection — the user's filtered/searched view.
export const DELIVERABLES_INDEX_TC = 'deliverablesIndex';

// Every deliverable of the project, unfiltered. Dependency questions ("what can
// this depend on", "what moves with this date") must be answered over the whole
// project, not over whatever the list is currently filtered to.
export const DELIVERABLES_GRAPH_TC = 'DELIVERABLE_DEPENDENCY_GRAPH';

// Helpers over the SERVER-computed deliverable fields. The backend returns
// workOrderMetrics / taskMetrics / budgetUsed already computed on GET
// (openwrench-app ProjectDeliverableServiceImpl.hydrateEntity) — the client
// never derives these, it only formats them.

// Work-order buckets on a deliverable (distinct from the deliverable's own
// status). Labels come from the WO rollup, not the deliverable status enum.
export const WORK_ORDER_METRIC_ROWS = [
	{ key: 'workOrdersNotStarted', label: 'Not Started' },
	{ key: 'workOrdersInProgress', label: 'In Progress' },
	{ key: 'workOrdersClosed', label: 'Completed' },
	{ key: 'workOrdersPaid', label: 'Paid' },
];

export const workOrderMetricsTotal = (metrics): number =>
	WORK_ORDER_METRIC_ROWS.reduce((total, row) => total + nullSafeGetOrElse(row.key, metrics, 0), 0);

// Work orders considered "done" — closed + paid. Numerator for the "3/4" rollup.
export const workOrdersDoneCount = (metrics): number =>
	nullSafeGetOrElse('workOrdersClosed', metrics, 0) +
	nullSafeGetOrElse('workOrdersPaid', metrics, 0);

export const budgetPercentUsed = (budget, budgetUsed): number => {
	const total = Number(budget) || 0;
	if (total <= 0) {
		return 0;
	}
	return Math.round(((Number(budgetUsed) || 0) * 100) / total);
};

export const formatCurrency = (amount): string => `$${(Number(amount) || 0).toLocaleString()}`;

// --- Dependencies -----------------------------------------------------------
// A deliverable points at one other deliverable of the same project via
// dependsOnDeliverableId, so the graph is a forest: every node has at most one
// parent, and "depends on one of my own dependents" is the only way to make a
// cycle. The backend rejects cycles with a 400; these helpers let the UI keep the
// user out of that state and explain what a due-date move will drag along.

export const getDeliverableById = (deliverableId, deliverables) =>
	(deliverables || []).find(
		(deliverable) => `${nullSafeGet('id', deliverable)}` === `${deliverableId}`
	);

// The deliverable this one waits on. Prefers the API's hydrated parent if it ever
// sends one (immune to filtering and pagination), otherwise resolves the id against
// the project's full deliverable list — which must be the UNFILTERED one, or a
// parent the user filtered out reads as "no dependency".
export const getDependsOnDeliverable = (deliverable, deliverables) =>
	nullSafeGet('dependsOnDeliverable', deliverable) ||
	getDeliverableById(nullSafeGet('dependsOnDeliverableId', deliverable), deliverables);

// Deliverables that point straight at this one.
export const getDirectDependents = (deliverableId, deliverables) =>
	(deliverables || []).filter(
		(deliverable) => `${nullSafeGet('dependsOnDeliverableId', deliverable)}` === `${deliverableId}`
	);

// Everything downstream of this deliverable, at any depth — the set the backend
// shifts when its due date moves. `seen` guards against a cycle that somehow got
// stored, so a bad row can't spin this forever.
export const getAllDependents = (deliverableId, deliverables) => {
	const collected = [];
	const seen = new Set([`${deliverableId}`]);
	const walk = (currentId) => {
		getDirectDependents(currentId, deliverables).forEach((dependent) => {
			const dependentId = `${nullSafeGet('id', dependent)}`;
			if (seen.has(dependentId)) {
				return;
			}
			seen.add(dependentId);
			collected.push(dependent);
			walk(dependentId);
		});
	};
	walk(deliverableId);
	return collected;
};

// Valid "depends on" options for a deliverable: every other deliverable in the
// project except its own dependents, since depending on a dependent is a cycle.
export const getDependencyOptions = (deliverableId, deliverables) => {
	if (!deliverableId) {
		return deliverables || [];
	}
	const excluded = new Set([
		`${deliverableId}`,
		...getAllDependents(deliverableId, deliverables).map(
			(dependent) => `${nullSafeGet('id', dependent)}`
		),
	]);
	return (deliverables || []).filter(
		(deliverable) => !excluded.has(`${nullSafeGet('id', deliverable)}`)
	);
};
